import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Section {
  id?: string;
  page: string;
  section_key: string;
  title: string;
  subtitle: string;
  body: string;
  image_url: string;
  button_text: string;
  button_link: string;
  is_active: boolean;
  sort_order: number;
}

const pageSections: Record<string, { key: string; label: string }[]> = {
  home: [
    { key: 'featured', label: 'Produtos em Destaque' },
    { key: 'regions', label: 'Regiões Escocesas' },
    { key: 'whiskies', label: 'Whiskies Escoceses' },
    { key: 'gins', label: 'Gins Escoceses' },
    { key: 'brand', label: 'Seção da Marca' },
    { key: 'newsletter', label: 'Newsletter' },
  ],
  pages: [
    { key: 'our-story-hero', label: 'Nossa História — Banner' },
    { key: 'our-story-body', label: 'Nossa História — Conteúdo' },
    { key: 'the-estate-hero', label: 'The Estate — Banner' },
    { key: 'the-estate-body', label: 'The Estate — Conteúdo' },
    { key: 'speyside-hero', label: 'Speyside — Banner' },
    { key: 'speyside-body', label: 'Speyside — Conteúdo' },
    { key: 'highlands-hero', label: 'Highlands — Banner' },
    { key: 'highlands-body', label: 'Highlands — Conteúdo' },
    { key: 'whiskies-hero', label: 'Whiskies — Banner' },
    { key: 'gins-hero', label: 'Gins — Banner' },
    { key: 'contact-hero', label: 'Contato — Banner' },
  ],
};

const emptySection = (page: string, key: string, order: number): Section => ({
  page,
  section_key: key,
  title: '',
  subtitle: '',
  body: '',
  image_url: '',
  button_text: '',
  button_link: '',
  is_active: true,
  sort_order: order,
});

export default function AdminSectionsPage() {
  const { type } = useParams<{ type: string }>();
  const pageType = type === 'pages' ? 'pages' : 'home';
  const sections = pageSections[pageType] || [];

  const [data, setData] = useState<Record<string, Section>>({});
  const [activeKey, setActiveKey] = useState(sections[0]?.key || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSections();
  }, [pageType]);

  const loadSections = async () => {
    setLoading(true);
    const keys = sections.map(s => s.key);
    const { data: rows } = await supabase
      .from('site_sections')
      .select('*')
      .in('section_key', keys);

    const map: Record<string, Section> = {};
    sections.forEach((s, i) => {
      const row = rows?.find(r => r.section_key === s.key);
      map[s.key] = row
        ? { ...row }
        : emptySection(pageType, s.key, i);
    });
    setData(map);
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const section = data[activeKey];
    if (!section) { setSaving(false); return; }

    const payload = {
      page: pageType,
      section_key: activeKey,
      title: section.title,
      subtitle: section.subtitle,
      body: section.body,
      image_url: section.image_url,
      button_text: section.button_text,
      button_link: section.button_link,
      is_active: section.is_active,
      sort_order: section.sort_order,
      updated_at: new Date().toISOString(),
    };

    if (section.id) {
      await supabase.from('site_sections').update(payload).eq('id', section.id);
    } else {
      const { data: inserted } = await supabase.from('site_sections').insert(payload).select().maybeSingle();
      if (inserted) {
        setData(prev => ({ ...prev, [activeKey]: { ...prev[activeKey], id: inserted.id } }));
      }
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const update = (field: keyof Section, value: string | boolean | number) => {
    setData(prev => ({ ...prev, [activeKey]: { ...prev[activeKey], [field]: value } }));
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors";
  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );

  const current = data[activeKey];
  const activeLabel = sections.find(s => s.key === activeKey)?.label || '';

  if (loading) return <div className="flex items-center justify-center h-40"><i className="ri-loader-4-line text-[#C9A84C] text-2xl animate-spin"></i></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">
            {pageType === 'home' ? 'Seções da Home' : 'Páginas Internas'}
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">Edite cada seção individualmente</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Salvando...</> : saved ? <><i className="ri-check-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar Seção</>}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Section list */}
        <div className="w-56 shrink-0">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {sections.map((s) => {
              const sec = data[s.key];
              return (
                <button
                  key={s.key}
                  onClick={() => setActiveKey(s.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-gray-50 last:border-0 transition-colors cursor-pointer ${activeKey === s.key ? 'bg-[#C9A84C]/10 text-[#C9A84C]' : 'hover:bg-gray-50 text-gray-600'}`}
                >
                  <div className={`w-2 h-2 rounded-full shrink-0 ${sec?.is_active ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                  <span className="text-xs font-medium leading-tight">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Editor */}
        {current && (
          <div className="flex-1 space-y-5">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
                <h2 className="text-sm font-semibold text-gray-700">{activeLabel}</h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-500">Seção ativa</span>
                  <div
                    onClick={() => update('is_active', !current.is_active)}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${current.is_active ? 'bg-emerald-400' : 'bg-gray-200'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${current.is_active ? 'left-5' : 'left-0.5'}`} />
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Field label="Título">
                  <input className={inputCls} value={current.title} onChange={e => update('title', e.target.value)} placeholder="Título da seção" />
                </Field>
                <Field label="Subtítulo">
                  <input className={inputCls} value={current.subtitle} onChange={e => update('subtitle', e.target.value)} placeholder="Subtítulo ou tagline" />
                </Field>
                <Field label="Texto / Descrição">
                  <textarea className={inputCls} rows={4} value={current.body} onChange={e => update('body', e.target.value)} placeholder="Texto descritivo da seção..." maxLength={500} />
                  <p className="text-xs text-gray-400 mt-1">{current.body.length}/500 caracteres</p>
                </Field>
                <Field label="URL da Imagem">
                  <input className={inputCls} value={current.image_url} onChange={e => update('image_url', e.target.value)} placeholder="https://..." />
                </Field>
                {current.image_url && (
                  <div className="rounded-lg overflow-hidden h-32 border border-gray-100">
                    <img src={current.image_url} alt="preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Texto do Botão">
                    <input className={inputCls} value={current.button_text} onChange={e => update('button_text', e.target.value)} placeholder="Ex: Saiba Mais" />
                  </Field>
                  <Field label="Link do Botão">
                    <input className={inputCls} value={current.button_link} onChange={e => update('button_link', e.target.value)} placeholder="/pagina" />
                  </Field>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
